import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { createWebHistory, createRouter } from 'vue-router';
import App from './App.vue';

// Import lazy loading helpers and components
import * as lazyLoaders from './router/lazyLoaders';
const {
    DummyView, LoginComponent, DashboardPage, LandingPage, ProgettoFinanziatoPage,
    TicketsList, TicketDetail, DashboardSla, AuditLog,
    ClienteDashboard, ClienteTickets, ClienteInterventi, ClienteRapportini,
    ClienteNotifiche, ClienteSupportoTecnico, ClienteDocumenti, ClienteOreResidue,
    ClienteProfilo, ClienteLavorazioni, ClienteLavorazioneDettaglio, CompletaAnagrafica,
    AnagraficheList, LavorazioniList, LavorazioneDettaglio, DispositiviList,
    ContrattiList, ContrattiTemplates, ContrattiAssign, ContrattoDetail,
    AssistenzaList, AssistenzaInternaForm, TecniciManagement, FornitoriList,
        AdminUsers,
        ServicesPage, ServiceTemplatesPage, ServiceCategoriesPage, ProvidersPage, ServiceDetail, ServiceCreate, AdminPermissionsPage,
    ApiDocsPage, TechnicalDocsPage,
    SettingsPage, AdminLavorazioni, AdminTickets, AdminPagamenti, AdminReport,
    AdminCalendar, AdminDocumenti, ClientePagamenti,
    CalendarComponent, ChatComponent, InboxComponent, ContactGrideComponent,
    DocumentsComponent, FileMediaComponent, FileImageComponent, InvoicesComponent,
    InvoicesTwoComponent, PricingComponent, TeamsBoardComponent, ProjectsListComponent,
    TimelineComponent, ProfileComponent, SearchComponent, FaqComponent,
    EChartsComponent, DataComponent
} = lazyLoaders;

// Import validation and plugins
import './vee-validate';
import '@/plugins/echarts';

// Import libraries
import VueToast from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import CKEditor from '@ckeditor/ckeditor5-vue';
import mitt from 'mitt';
import VTooltip from 'floating-vue';
import 'floating-vue/dist/style.css';

// Import store
import { useAuthStore } from './stores/auth';

// Create app instance
const app = createApp(App);
const pinia = createPinia();
const emitter = mitt();

app.config.globalProperties.emitter = emitter;
app.use(pinia);

// Define routes
const routes = [
    {
        path: '/',
        name: 'home',
        component: LandingPage,
        meta: {
            title: 'Home',
            requiresAuth: false,
        }
    },
    {
        path: '/progetto-finanziato',
        name: 'progetto-finanziato',
        component: ProgettoFinanziatoPage,
        meta: {
            title: 'Progetto finanziato',
            requiresAuth: false,
        }
    },
    {
        path: '/dashboard',
        name: 'dashboard',
        redirect: '/dashboard/index',
        component: DummyView,
        meta: {
            breadCrumb: 'Dashboard',
            requiresAuth: true,
        },
        children: [
            { 
                path: 'index', 
                component: DashboardPage, 
                meta: { 
                    breadCrumb: 'Home', 
                    title: 'Dashboard',
                    requiresAuth: true,
                }
            },
        ]
    },
    {
        path: '/anagrafiche',
        redirect: '/anagrafiche/list',
        component: DummyView,
        meta: {
            breadCrumb: 'Anagrafiche',
            requiresAuth: true,
        },
        children: [
            { 
                path: 'list', 
                component: AnagraficheList, 
                meta: { 
                    breadCrumb: 'Lista Clienti',
                    title: 'Gestione Clienti',
                    requiresAuth: true,
                }
            },
        ]
    },
    {
        path: '/lavorazioni',
        redirect: '/lavorazioni/list',
        component: DummyView,
        meta: {
            breadCrumb: 'Lavorazioni',
            requiresAuth: true,
        },
        children: [
            { 
                path: 'list', 
                component: LavorazioniList, 
                meta: { 
                    breadCrumb: 'Lista Lavorazioni',
                    title: 'Gestione Lavorazioni',
                    requiresAuth: true,
                }
            },
            { 
                path: ':id', 
                component: LavorazioneDettaglio, 
                meta: { 
                    breadCrumb: 'Dettaglio Lavorazione',
                    title: 'Dettaglio Lavorazione',
                    requiresAuth: true,
                }
            },
        ]
    },
    {
        path: '/dispositivi',
        redirect: '/dispositivi/list',
        component: DummyView,
        meta: {
            breadCrumb: 'Dispositivi',
            requiresAuth: true,
        },
        children: [
            { 
                path: 'list', 
                component: DispositiviList, 
                meta: { 
                    breadCrumb: 'Lista Dispositivi',
                    title: 'Gestione Dispositivi',
                    requiresAuth: true,
                }
            },
        ]
    },
    {
        path: '/contratti',
        redirect: '/contratti/list',
        component: DummyView,
        meta: {
            breadCrumb: 'Contratti',
            requiresAuth: true,
        },
        children: [
            { 
                path: 'list', 
                component: ContrattiList, 
                meta: { 
                    breadCrumb: 'Lista Contratti',
                    title: 'Gestione Contratti',
                    requiresAuth: true,
                }
            },
            {
                path: 'templates',
                component: ContrattiTemplates,
                meta: {
                    breadCrumb: 'Template Contratti',
                    title: 'Template Contratti',
                    requiresAuth: true,
                }
            },
            {
                path: 'assign',
                component: ContrattiAssign,
                meta: {
                    breadCrumb: 'Assegna Contratti',
                    title: 'Assegna Contratti',
                    requiresAuth: true,
                }
            },
            {
                path: 'view/:id',
                component: ContrattoDetail,
                meta: {
                    breadCrumb: 'Dettaglio Contratto',
                    title: 'Dettaglio Contratto',
                    requiresAuth: true,
                }
            },
        ]
    },
    {
        path: '/assistenza',
        redirect: '/assistenza/list',
        component: DummyView,
        meta: {
            breadCrumb: 'Assistenza',
            requiresAuth: true,
        },
        children: [
            { 
                path: 'list', 
                component: AssistenzaList, 
                meta: { 
                    breadCrumb: 'Storico Assistenza',
                    title: 'Gestione Assistenza',
                    requiresAuth: true,
                }
            },
            { 
                path: 'interna', 
                component: AssistenzaInternaForm, 
                meta: { 
                    breadCrumb: 'Nuova Assistenza Interna',
                    title: 'Assistenza Interna',
                    requiresAuth: true,
                }
            },
        ]
    },
    {
        path: '/tickets',
        redirect: '/tickets/list',
        component: DummyView,
        meta: { breadCrumb: 'Tickets', requiresAuth: true },
        children: [
            { path: 'list', component: TicketsList, meta: { breadCrumb: 'Lista Tickets', title: 'Tickets', requiresAuth: true } },
            { path: 'sla', component: DashboardSla, meta: { breadCrumb: 'SLA', title: 'SLA', requiresAuth: true } },
            { path: ':id', component: TicketDetail, meta: { breadCrumb: 'Dettaglio Ticket', title: 'Ticket', requiresAuth: true } },
        ]
    },
    {
        path: '/completa-anagrafica',
        name: 'completa-anagrafica',
        component: CompletaAnagrafica,
        meta: { title: 'Completa Profilo', requiresAuth: true }
    },
    {
        path: '/cliente',
        redirect: '/cliente/dashboard',
        component: DummyView,
        meta: { breadCrumb: 'Portale Cliente', requiresAuth: true },
        children: [
            { path: 'dashboard', component: ClienteDashboard, meta: { breadCrumb: 'Dashboard', title: 'Portale Cliente', requiresAuth: true } },
            { path: 'notifiche', component: ClienteNotifiche, meta: { breadCrumb: 'Notifiche', title: 'Notifiche', requiresAuth: true } },
            { path: 'supporto', component: ClienteSupportoTecnico, meta: { breadCrumb: 'Supporto', title: 'Supporto diretto', requiresAuth: true } },
            { path: 'ticket', component: ClienteTickets, meta: { breadCrumb: 'Ticket', title: 'Ticket Cliente', requiresAuth: true } },
            { path: 'interventi', component: ClienteInterventi, meta: { breadCrumb: 'Interventi', title: 'Interventi', requiresAuth: true } },
            { path: 'rapportini', component: ClienteRapportini, meta: { breadCrumb: 'Rapportini', title: 'Rapportini', requiresAuth: true } },
            { path: 'documenti', component: ClienteDocumenti, meta: { breadCrumb: 'Documenti', title: 'Documenti', requiresAuth: true } },
            { path: 'ore-residue', component: ClienteOreResidue, meta: { breadCrumb: 'Ore Residue', title: 'Ore Residue', requiresAuth: true } },
            { path: 'lavorazioni', component: ClienteLavorazioni, meta: { breadCrumb: 'Lavorazioni', title: 'Le tue Lavorazioni', requiresAuth: true } },
            { path: 'lavorazioni/:id', component: ClienteLavorazioneDettaglio, meta: { breadCrumb: 'Dettaglio Lavorazione', title: 'Dettaglio Lavorazione', requiresAuth: true } },
            { path: 'anagrafica', component: ClienteProfilo, meta: { breadCrumb: 'Anagrafica', title: 'Gestione Anagrafica', requiresAuth: true } },
            { path: 'profilo', component: ClienteProfilo, meta: { breadCrumb: 'Profilo', title: 'Il mio Profilo', requiresAuth: true } },
            { path: 'pagamenti', component: ClientePagamenti, meta: { breadCrumb: 'Storico pagamenti', title: 'Storico Pagamenti', requiresAuth: true } },
        ]
    },
    {
        path: '/admin',
        component: DummyView,
        meta: {
            breadCrumb: 'Admin',
            requiresAuth: true,
        },
        children: [
            {
                path: 'tecnici',
                component: TecniciManagement,
                meta: {
                    breadCrumb: 'Gestione Tecnici',
                    title: 'Gestione Tecnici',
                    requiresAuth: true,
                }
            },
            {
                path: 'users',
                    component: AdminUsers,
                meta: {
                    breadCrumb: 'Gestione Utenti',
                    title: 'Gestione Utenti',
                    requiresAuth: true,
                }
            },
            {
                path: 'audit',
                component: AuditLog,
                meta: {
                    breadCrumb: 'Audit Log',
                    title: 'Audit Log',
                    requiresAuth: true,
                }
            },
            {
                path: 'api-docs',
                component: ApiDocsPage,
                meta: {
                    breadCrumb: 'API Docs',
                    title: 'Documentazione API',
                    requiresAuth: true,
                }
            },
            {
                path: 'technical-docs',
                component: TechnicalDocsPage,
                meta: {
                    breadCrumb: 'Documentazione Tecnica',
                    title: 'Documentazione Tecnica API',
                    requiresAuth: true,
                }
            },
            {
                path: 'api-documentation-manager',
                component: () => import('./components/admin/pages/ApiDocumentationManager.vue'),
                meta: {
                    breadCrumb: 'Gestione Documentazione API',
                    title: 'Gestione Documentazione API',
                    requiresAuth: true,
                }
            },
            {
                path: 'lavorazioni',
                component: AdminLavorazioni,
                meta: {
                    breadCrumb: 'Monitoraggio Commesse',
                    title: 'Monitoraggio Commesse',
                    requiresAuth: true,
                }
            },
            {
                path: 'ticket',
                component: AdminTickets,
                meta: {
                    breadCrumb: 'Gestione Ticket',
                    title: 'Gestione Ticket',
                    requiresAuth: true,
                }
            },
            {
                path: 'pagamenti',
                component: AdminPagamenti,
                meta: {
                    breadCrumb: 'Gestione Pagamenti',
                    title: 'Gestione Pagamenti',
                    requiresAuth: true,
                }
            },
            {
                path: 'report',
                component: AdminReport,
                meta: {
                    breadCrumb: 'Reportistica e KPI',
                    title: 'Reportistica e KPI',
                    requiresAuth: true,
                }
            },
            {
                path: 'calendar',
                component: AdminCalendar,
                meta: {
                    breadCrumb: 'Calendario e Scadenze',
                    title: 'Calendario e Scadenze',
                    requiresAuth: true,
                }
            },
            {
                path: 'documenti',
                component: AdminDocumenti,
                meta: {
                    breadCrumb: 'Gestione Documentale',
                    title: 'Gestione Documentale',
                    requiresAuth: true,
                }
            },
            {
                path: 'permissions',
                component: AdminPermissionsPage,
                meta: {
                    breadCrumb: 'Gestione Permessi',
                    title: 'Gestione Permessi',
                    requiresAuth: true,
                }
            }
        ]
    },
    {
        path: '/admin/settings',
        component: SettingsPage,
        meta: { breadCrumb: 'Impostazioni', title: 'Impostazioni', requiresAuth: true }
    },
    {
        path: '/fornitori',
        name: 'fornitori',
        component: FornitoriList,
        meta: { title: 'Fornitori', requiresAuth: true }
    },
    {
        path: '/services',
        name: 'services',
        component: ServicesPage,
        meta: { title: 'Servizi', requiresAuth: true }
    },
    {
        path: '/services/:id',
        name: 'service.detail',
        component: ServiceDetail,
        meta: { title: 'Dettaglio Servizio', breadCrumb: 'Dettaglio Servizio', requiresAuth: true }
    },
    {
        path: '/services/create',
        name: 'service.create',
        component: ServiceCreate,
        meta: { title: 'Nuovo Servizio', breadCrumb: 'Nuovo Servizio', requiresAuth: true }
    },
    {
        path: '/service-templates',
        name: 'service.templates',
        component: ServiceTemplatesPage,
        meta: { title: 'Template Servizi', requiresAuth: true }
    },
    {
        path: '/service-categories',
        name: 'service.categories',
        component: ServiceCategoriesPage,
        meta: { title: 'Categorie Servizi', requiresAuth: true }
    },
    {
        path: '/providers',
        name: 'providers',
        component: ProvidersPage,
        meta: { title: 'Provider', requiresAuth: true }
    },
    {
        path: '/authentication',
        redirect: '/authentication/page-login',
        component: DummyView,
        children: [
            { 
                path: 'page-login', 
                component: LoginComponent,
                name: 'login', 
                meta: { 
                    title: 'Login',
                    requiresAuth: false,
                }
            },
        ]
    },
    {
        path: '/app',
        redirect: '/app/app-calendar',
        component: DummyView,
        meta: {
            breadCrumb: 'App',
            requiresAuth: true,
        },
        children: [
            {
                path: 'app-calendar',
                component: CalendarComponent,
                meta: {
                    breadCrumb: 'Calendario',
                    title: 'Calendario Appuntamenti',
                    requiresAuth: true,
                }
            },
            {
                path: 'app-chat',
                component: ChatComponent,
                meta: {
                    breadCrumb: 'Chat',
                    title: 'Chat',
                    requiresAuth: true,
                }
            },
            {
                path: 'app-inbox',
                component: InboxComponent,
                meta: {
                    breadCrumb: 'Inbox',
                    title: 'Email',
                    requiresAuth: true,
                }
            },
            {
                path: 'app-contact-grid',
                component: ContactGrideComponent,
                meta: {
                    breadCrumb: 'Contatti',
                    title: 'Gestione Utenti',
                    requiresAuth: true,
                }
            },
        ]
    },
    {
        path: '/file-manager',
        redirect: '/file-manager/file-documents',
        component: DummyView,
        meta: {
            breadCrumb: 'Documenti',
            requiresAuth: true,
        },
        children: [
            {
                path: 'file-documents',
                component: DocumentsComponent,
                meta: {
                    breadCrumb: 'Documenti',
                    title: 'Archivio Documenti',
                    requiresAuth: true,
                }
            },
            {
                path: 'file-media',
                component: FileMediaComponent,
                meta: {
                    breadCrumb: 'Media',
                    title: 'Media',
                    requiresAuth: true,
                }
            },
            {
                path: 'file-images',
                component: FileImageComponent,
                meta: {
                    breadCrumb: 'Immagini',
                    title: 'Immagini',
                    requiresAuth: true,
                }
            },
        ]
    },
    {
        path: '/pages',
        redirect: '/pages/page-profile',
        component: DummyView,
        meta: {
            breadCrumb: 'Pagine',
            requiresAuth: true,
        },
        children: [
            {
                path: 'page-invoices',
                component: InvoicesComponent,
                meta: {
                    breadCrumb: 'Fatture',
                    title: 'Fatture',
                    requiresAuth: true,
                }
            },
            {
                path: 'page-invoices2',
                component: InvoicesTwoComponent,
                meta: {
                    breadCrumb: 'Preventivi',
                    title: 'Preventivi',
                    requiresAuth: true,
                }
            },
            {
                path: 'page-pricing',
                component: PricingComponent,
                meta: {
                    breadCrumb: 'Listino',
                    title: 'Listino Prezzi',
                    requiresAuth: true,
                }
            },
            {
                path: 'page-teams-board',
                component: TeamsBoardComponent,
                meta: {
                    breadCrumb: 'Board Ticket',
                    title: 'Board Ticket',
                    requiresAuth: true,
                }
            },
            {
                path: 'page-projects-list',
                component: ProjectsListComponent,
                meta: {
                    breadCrumb: 'Lista Progetti',
                    title: 'Lista Progetti',
                    requiresAuth: true,
                }
            },
            {
                path: 'page-timeline',
                component: TimelineComponent,
                meta: {
                    breadCrumb: 'Timeline',
                    title: 'Timeline',
                    requiresAuth: true,
                }
            },
            {
                path: 'page-profile',
                component: ProfileComponent,
                meta: {
                    breadCrumb: 'Profilo',
                    title: 'Profilo Utente',
                    requiresAuth: true,
                }
            },
            {
                path: 'page-search-results',
                component: SearchComponent,
                meta: {
                    breadCrumb: 'Ricerca',
                    title: 'Risultati Ricerca',
                    requiresAuth: true,
                }
            },
            {
                path: 'page-faq',
                component: FaqComponent,
                meta: {
                    breadCrumb: 'FAQ',
                    title: 'Domande Frequenti',
                    requiresAuth: true,
                }
            },
        ]
    },
    {
        path: '/charts',
        redirect: '/charts/chart-echarts',
        component: DummyView,
        meta: {
            breadCrumb: 'Grafici',
            requiresAuth: true,
        },
        children: [
            {
                path: 'chart-echarts',
                component: EChartsComponent,
                meta: {
                    breadCrumb: 'Statistiche',
                    title: 'Statistiche & Report',
                    requiresAuth: true,
                }
            },
        ]
    },
    {
        path: '/widgets',
        redirect: '/widgets/widgets-data',
        component: DummyView,
        meta: {
            breadCrumb: 'Widget',
            requiresAuth: true,
        },
        children: [
            {
                path: 'widgets-data',
                component: DataComponent,
                meta: {
                    breadCrumb: 'Widget Dati',
                    title: 'Widget Dati',
                    requiresAuth: true,
                }
            },
            {
                path: 'sla',
                component: DashboardSla,
                meta: {
                    breadCrumb: 'SLA',
                    title: 'SLA KPI',
                    requiresAuth: true
                }
            }
        ]
    },
    {
        path: '/:pathMatch(.*)*',
        redirect: '/'
    }
];

// Create router
const router = createRouter({
    history: createWebHistory(),
    routes,
});

// Runtime safety: replace any route entries with falsy `component` to avoid hydration errors
function sanitizeRoutes(routeList) {
    routeList.forEach(r => {
        if (!r.component) {
            r.component = DummyView;
        }
        if (Array.isArray(r.children) && r.children.length) sanitizeRoutes(r.children);
    });
}
sanitizeRoutes(routes);

// Navigation guard for authentication
router.beforeEach(async (to, from, next) => {
    // Set page title
    document.title = `ByteRivo :: ${to.meta.title || 'Gestionale IT'}`;
    
    const authStore = useAuthStore();
    // If the Pinia store isn't marked authenticated but a token exists in localStorage,
    // restore the minimal auth state so route guards don't force a login on every navigation.
    try{
        const token = localStorage.getItem('jwt_token');
        const storedUser = localStorage.getItem('user');
        
        if (!authStore.isLoggedIn && token) {
            // Restore token into the store
            authStore.token = token;

            // If we have cached user info, restore it (do NOT mark authenticated yet)
            if (storedUser) {
                try {
                    const parsedUser = JSON.parse(storedUser);
                    authStore.user = parsedUser;
                } catch (e) {
                    authStore.user = null;
                }
            }

            // Always validate the token against the backend (fetchUser).
            // If the backend is unavailable (503) or the token is invalid, clear auth.
            try {
                await authStore.fetchUser();
                authStore.isAuthenticated = !!authStore.user;
            } catch (e) {
                // Clear local auth state and storage to prevent cached credentials from granting access
                try { localStorage.removeItem('jwt_token'); } catch (_) { /* ignore errors when clearing storage */ }
                try { localStorage.removeItem('user'); } catch (_) { /* ignore errors when clearing storage */ }
                authStore.token = null;
                authStore.user = null;
                authStore.isAuthenticated = false;
            }
        }
    } catch (e) { console.warn(e); }
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth !== false);
    
    // Se la rotta richiede autenticazione
    if (requiresAuth && to.name !== 'login') {
        if (!authStore.isLoggedIn) {
            // Non autenticato, reindirizza al login
            next({ name: 'login' });
        } else {
            // Autenticato - controlla permessi per rotte admin
            const isAdminRoute = to.path.startsWith('/dashboard') || 
                                to.path.startsWith('/anagrafiche') ||
                                to.path.startsWith('/lavorazioni') ||
                                to.path.startsWith('/dispositivi') ||
                                to.path.startsWith('/contratti') ||
                                to.path.startsWith('/assistenza') ||
                                to.path.startsWith('/tecnici') ||
                                to.path.startsWith('/fornitori') ||
                                to.path.startsWith('/settings') ||
                                to.path.startsWith('/tickets') ||
                                to.path.startsWith('/sla') ||
                                to.path.startsWith('/audit') ||
                                to.path.startsWith('/admin') ||
                                to.path.startsWith('/app-') ||
                                to.path.startsWith('/file-manager');
            
            // Se è una rotta admin, controlla il permesso 'admin.access'
            if (isAdminRoute && !authStore.hasPermission('admin.access')) {
                // Redirigere al portale cliente
                next({ path: '/cliente/dashboard' });
            } else if (to.name === 'login' && authStore.isLoggedIn) {
                // Se già autenticato e va al login, redirigere in base al ruolo
                if (authStore.userRoles.includes('cliente')) {
                    next({ path: '/cliente/dashboard' });
                } else {
                    next({ path: '/dashboard/index' });
                }
            } else {
                // Autenticato e permessi ok, verifica anagrafica se utente è cliente
                const anagraficaCompletata = authStore.user?.anagrafica_completata;
                const needsProfileCompletion = !anagraficaCompletata || anagraficaCompletata === 0 || anagraficaCompletata === "0";
                
                if (authStore.userRoles.includes('cliente') && 
                    authStore.user && 
                    needsProfileCompletion && 
                    to.path !== '/completa-anagrafica') {
                    // Utente cliente senza anagrafica completata: redirigere al form
                    next({ path: '/completa-anagrafica' });
                } else {
                    next();
                }
            }
        }
    } else if (to.name === 'login' && authStore.isLoggedIn) {
        // Se già autenticato e va al login, redirigere in base al ruolo
        if (authStore.userRoles.includes('cliente')) {
            next({ path: '/cliente/dashboard' });
        } else {
            next({ path: '/dashboard/index' });
        }
    } else {
        // Nessuna restrizione, procedi
        next();
    }
});

// Use plugins and mount app
app.use(router);
app.use(VueToast);
app.use(VTooltip);
app.use(CKEditor);
app.use(emitter);

app.mount('#app');

// Gestione globale evento auth:unauthorized inviato dall'interceptor axios
window.addEventListener('auth:unauthorized', async () => {
    try {
        const authStore = useAuthStore();
        // Pulisci lo stato locale senza chiamare l'API di logout (evita ricorsione di 401)
        if (authStore) {
            authStore.user = null;
            authStore.token = null;
            authStore.isAuthenticated = false;
        }
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('user');
    } catch (e) {
        console.warn(e);
    }
    try {
        router.push({ name: 'login' });
    } catch (e) {
        // se il router non è pronto, fallback a location
        window.location.href = '/authentication/page-login';
    }
});
